/**
 * Life4Earth V2 — Feed Share API Adapter
 *
 * Endpoints for sharing content (links, videos, chronicles) to community feeds.
 *
 *   GET  /api/News/preview?url=...          → OG/oEmbed metadata preview
 *   POST /api/News/share                    → submit a share to a community feed
 */

import { apiFetch } from './client';

// ── Types ──────────────────────────────────────────────────

export type ShareType = 'link' | 'video' | 'chronicle' | 'text';

export interface FeedShareRequest {
  scopeGuid: string;
  shareType: ShareType;
  url?: string;
  chronicleGuid?: string;
  comment?: string;
  /** Curator-selected image URL override (from page image picker). */
  imageUrlOverride?: string;
  /**
   * DEV-966 — Curator-edited intro text. When set, replaces the server's
   * scraped description (og:description / twitter:description / meta
   * description / JSON-LD / first-paragraph fallback). Lets the curator
   * provide their own summary when the page lacks useful metadata.
   */
  excerptOverride?: string;
  /**
   * DEV-967 — Curator-edited title. Parallels excerptOverride. Replaces
   * the server's scraped title when the curator types into the Title field
   * in the Add Web Link modal.
   */
  titleOverride?: string;
}

export interface UrlPreview {
  url: string;
  title?: string;
  description?: string;
  imageUrl?: string;
  siteName?: string;
  embedHtml?: string;
  embedType?: string;
  /** Original publish date extracted from article:published_time OG tag. */
  publishedAt?: string;
  /** Author extracted from article:author meta tag. */
  author?: string;
  /** Additional images found on the page (for image picker). */
  images?: string[];
}

export interface FeedShareResponse {
  moderationGuid: string;
  newsItemGuid: string;
  scopeType: string;
  scopeGuid: string;
  status: string;
  contentType: string;
  placement?: string;
  /** DEV-1441 Voice moment — true exactly once, on the user's first-ever
   *  feed share. The client shows the one-time Voice ribbon on this. */
  firstPost?: boolean;
  article: {
    newsItemGuid: string;
    contentType: string;
    canonicalUrl: string;
    title: string;
    excerpt?: string;
    sourceName?: string;
    imageUrl?: string;
    category?: string;
    sharedBy?: string;
    shareComment?: string;
    chronicleGuid?: string;
    ogTitle?: string;
    ogDescription?: string;
    ogImageUrl?: string;
    ogSiteName?: string;
    embedHtml?: string;
    embedType?: string;
  };
}

// ── Preview ────────────────────────────────────────────────

/** Fetch OpenGraph / oEmbed metadata for a URL. */
export async function previewUrl(url: string): Promise<UrlPreview> {
  const qs = new URLSearchParams({ url });
  return apiFetch<UrlPreview>(`/api/News/preview?${qs.toString()}`);
}

// ── Share ──────────────────────────────────────────────────

/** Submit a share (link, video, or chronicle) to a community feed. */
export async function shareToCommunityFeed(req: FeedShareRequest): Promise<FeedShareResponse> {
  return apiFetch<FeedShareResponse>('/api/News/share', {
    method: 'POST',
    body: JSON.stringify(req),
  });
}
